import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Alert, Box, Card, CardContent, CardMedia, Chip, Container, Stack, Typography } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import Loading from "../components/Shared/Loading";
import NotFound from "../components/Shared/NotFound";
import PageLayout from "../components/Shared/PageLayout";
import Footer from "../components/Shared/Footer";
import Header from "../components/Shared/Header";
import Kunde1Header from "../customers/kunde1/components/Header";
import Kunde2Header from "../customers/kunde2/components/Header";
import {
  getContentVisibilityLevel,
  isContentListed,
  isContentVisibleForAccess
} from "../content/contentClient";
import { getCustomerKey } from "../utils/domain";
import useWPCategoryPosts from "../hooks/useWPCategoryPosts";
import useWPPost from "../hooks/useWPPost";
import SeoHead from "../components/Shared/SeoHead";
import useLanguage from "../hooks/useLanguage";
import { getBlogItemPath } from "../utils/blogRoutes";

type WPTerm = {
  id?: number;
  taxonomy?: string;
  name?: string;
  slug?: string;
};

const getHeaderForCustomer = () => {
  const key = getCustomerKey();
  if (key === "kunde1") return <Kunde1Header />;
  if (key === "kunde2") return <Kunde2Header />;
  return <Header />;
};

const getFeaturedImage = (post: { _embedded?: any }) =>
  post?._embedded?.["wp:featuredmedia"]?.[0]?.media_details?.sizes?.large?.source_url ||
  post?._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
  "";

const getCategories = (post: { _embedded?: any }): WPTerm[] => {
  const termGroups = (post?._embedded?.["wp:term"] as WPTerm[][]) || [];
  const categoryGroup = termGroups.find((group) =>
    group.some((term) => term.taxonomy === "category")
  );
  return categoryGroup || [];
};

export default function BlogSlug() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const category = useWPCategoryPosts(slug, 12, lang);
  const post = useWPPost(slug, lang);
  const listedCategoryPosts = category.posts.filter((item) => isContentListed(item));
  const visuallyHiddenSx = {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0
  };

  if (category.loading && post.loading) return <Loading message="Inhalt wird geladen..." />;

  const hasCategory = !category.error;
  const hasPost = Boolean(post.post) && !post.notFound;

  if (hasCategory) {
    const categoryTitle = category.categoryName || slug;
    const showH1 = category.category?.acf?.show_h1 ?? true;
    const hideH1Visually = category.category?.acf?.hide_h1_visually ?? false;
    const renderVisibleH1 = showH1 && !hideH1Visually;
    const renderHiddenH1 = hideH1Visually;
    const seoTitle = `Kategorie: ${categoryTitle}`;
    const seoDescription = `Artikel in der Kategorie ${categoryTitle}.`;
    const canonicalUrl = typeof window !== "undefined" ? window.location.href : "";
    return (
      <PageLayout header={getHeaderForCustomer()} footer={<Footer />}>
        <SeoHead
          title={seoTitle}
          description={seoDescription}
          ogTitle={seoTitle}
          ogDescription={seoDescription}
          canonicalUrl={canonicalUrl}
          robots="index"
        />
        <Box component="main" sx={{ py: { xs: 4, md: 6 } }}>
          <Container maxWidth="lg">
            <Stack spacing={2} sx={{ mb: 4 }}>
              {renderHiddenH1 ? (
                <Typography variant="h1" component="h1" sx={visuallyHiddenSx}>
                  Kategorie: {categoryTitle}
                </Typography>
              ) : null}
              {renderVisibleH1 ? <Typography variant="h1">Kategorie: {categoryTitle}</Typography> : null}
              <Typography variant="body1" color="text.secondary">
                {listedCategoryPosts.length} Beitrag{listedCategoryPosts.length === 1 ? "" : "e"}
              </Typography>
            </Stack>
            {hasPost ? (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Hinweis: Es gibt sowohl eine Kategorie als auch einen Beitrag mit dem gleichen Slug. Bitte eindeutige
                Slugs verwenden.
              </Alert>
            ) : null}
            <Box
              sx={{
                display: "grid",
                gap: 3,
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }
              }}
            >
              {listedCategoryPosts.map((item) => {
                const image = getFeaturedImage(item);
                const categories = getCategories(item);
                const visibility = getContentVisibilityLevel(item);
                const isLocked = !isContentVisibleForAccess(item, []);
                const previewHtml = isLocked
                  ? `<p>${item.acf?.access_teaser_message || "Dieser Inhalt ist nur mit Freischaltung sichtbar."}</p>`
                  : item.excerpt.rendered;
                return (
                  <Card
                    key={item.id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 180ms ease, box-shadow 180ms ease",
                      "&:hover .blog-card-media": { transform: "scale(1.04)" },
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 20px 45px rgba(11, 31, 39, 0.15)"
                      }
                    }}
                  >
                    {image ? (
                      <Box sx={{ overflow: "hidden" }}>
                        <CardMedia
                          component="img"
                          height="180"
                          image={image}
                          alt={item.title.rendered}
                          className="blog-card-media"
                          sx={{ transition: "transform 220ms ease" }}
                        />
                      </Box>
                    ) : null}
                    <CardContent>
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                        {isLocked ? (
                          <Chip
                            icon={<LockOutlinedIcon />}
                            label={visibility === "private" ? "Privat" : "Premium"}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        ) : null}
                        {categories.slice(0, 2).map((term) => (
                          <Chip
                            key={term.id || term.slug}
                            label={term.name}
                            size="small"
                            component={Link}
                          to={getBlogItemPath(term.slug || "", lang)}
                            clickable
                          />
                        ))}
                      </Stack>
                      <Typography
                        variant="h5"
                        component={Link}
                        to={getBlogItemPath(item.slug, lang)}
                        sx={{
                          textDecoration: "none",
                          color: "text.primary",
                          display: "inline-block",
                          transition: "color 180ms ease",
                          "&:hover": { color: "primary.main" }
                        }}
                        dangerouslySetInnerHTML={{ __html: item.title.rendered }}
                      />
                      <Box
                        sx={{ mt: 1, color: "text.secondary" }}
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                      />
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          component={Link}
                          to={getBlogItemPath(item.slug, lang)}
                          sx={{
                            textDecoration: "none",
                            color: "primary.main",
                            fontWeight: 600,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5
                          }}
                        >
                          {isLocked ? "Mehr erfahren" : "Mehr lesen"} <span aria-hidden="true">-&gt;</span>
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Container>
        </Box>
      </PageLayout>
    );
  }

  if (post.loading) return <Loading message="Beitrag wird geladen..." />;
  if (post.notFound || !post.post) return <NotFound message="Beitrag nicht gefunden." />;

  const image = getFeaturedImage(post.post);
  const categories = getCategories(post.post);
  const author = post.post?._embedded?.author?.[0]?.name || "";
  const date = new Date(post.post.date);
  const dateLabel = Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
  const acf = post.post.acf || {};
  const seoTitle = acf.seo_title || post.post.title.rendered;
  const seoDescription = acf.seo_description || post.post.excerpt?.rendered?.replace(/<[^>]+>/g, "") || "";
  const ogTitle = acf.og_title || seoTitle;
  const ogDescription = acf.og_description || seoDescription;
  const ogImage = acf.og_image || image || "";
  const canonicalUrl = acf.canonical_url || (typeof window !== "undefined" ? window.location.href : "");
  const isLockedPost = !isContentVisibleForAccess(post.post, []);
  const lockedVisibility = getContentVisibilityLevel(post.post);
  const robots = acf.robots || (isLockedPost ? "noindex" : "index");
  const showH1 = acf.show_h1 ?? true;
  const hideH1Visually = acf.hide_h1_visually ?? false;
  const renderVisibleH1 = showH1 && !hideH1Visually;
  const renderHiddenH1 = hideH1Visually;

  return (
    <PageLayout header={getHeaderForCustomer()} footer={<Footer />}>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        ogTitle={ogTitle}
        ogDescription={ogDescription}
        ogImage={ogImage}
        canonicalUrl={canonicalUrl}
        robots={robots}
      />
      <Box component="main" sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Stack spacing={2}>
            {image ? (
              <CardMedia component="img" image={image} alt={post.post.title.rendered} sx={{ borderRadius: 2 }} />
            ) : null}
            {renderHiddenH1 ? (
              <Typography variant="h1" component="h1" sx={visuallyHiddenSx}>
                <span dangerouslySetInnerHTML={{ __html: post.post.title.rendered }} />
              </Typography>
            ) : null}
            {renderVisibleH1 ? (
              <Typography variant="h1" dangerouslySetInnerHTML={{ __html: post.post.title.rendered }} />
            ) : null}
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              {isLockedPost ? (
                <Chip
                  icon={<LockOutlinedIcon />}
                  label={lockedVisibility === "private" ? "Privater Inhalt" : "Premium Inhalt"}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              ) : null}
              {dateLabel || author ? (
                <Typography variant="caption" color="text.secondary">
                  {[dateLabel, author].filter(Boolean).join(" - ")}
                </Typography>
              ) : null}
              {categories.slice(0, 3).map((term) => (
                <Chip
                  key={term.id || term.slug}
                  label={term.name}
                  size="small"
                  component={Link}
                  to={getBlogItemPath(term.slug || "", lang)}
                  clickable
                />
              ))}
            </Stack>
            {isLockedPost ? (
              <Box
                sx={{
                  p: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  bgcolor: "background.paper"
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {acf.access_teaser_message || "Dieser Inhalt ist Teil eines geschuetzten Bereichs und wird erst nach Freischaltung sichtbar."}
                </Typography>
              </Box>
            ) : (
              <Box
                component="article"
                sx={{
                  "& h2": { mt: 3, mb: 1.5, fontWeight: 700 },
                  "& h3": { mt: 2.5, mb: 1, fontWeight: 700 },
                  "& p": { mb: 2, color: "text.secondary" },
                  "& a": { color: "primary.main", fontWeight: 600 },
                  "& ul, & ol": { pl: 3, mb: 2, color: "text.secondary" },
                  "& img": { maxWidth: "100%", height: "auto", display: "block", borderRadius: 2 }
                }}
                dangerouslySetInnerHTML={{ __html: post.post.content.rendered }}
              />
            )}
          </Stack>
        </Container>
      </Box>
    </PageLayout>
  );
}
